import { useEffect, useState } from 'react'

export function useMediaQuery(query: string): boolean {
  const [state, setState] = useState(() => ({
    query,
    matches: typeof window !== 'undefined' && window.matchMedia(query).matches,
  }))

  // Sync matches when query changes (adjust-state-during-render pattern)
  if (state.query !== query) {
    setState({
      query,
      matches: typeof window !== 'undefined' && window.matchMedia(query).matches,
    })
  }

  useEffect(() => {
    if (typeof window === 'undefined') return

    const media = window.matchMedia(query)
    const listener = (e: MediaQueryListEvent) =>
      setState((prev) => ({ ...prev, matches: e.matches }))
    media.addEventListener('change', listener)

    return () => media.removeEventListener('change', listener)
  }, [query])

  return state.matches
}

export const breakpoints = {
  xs: '(max-width: 359px)',
  sm: '(min-width: 360px) and (max-width: 479px)',
  md: '(min-width: 480px) and (max-width: 639px)',
  tablet: '(min-width: 640px) and (max-width: 767px)',
  laptop: '(min-width: 768px) and (max-width: 1023px)',
  desktop: '(min-width: 1024px) and (max-width: 1279px)',
  xl: '(min-width: 1280px)',
  xxl: '(min-width: 1440px)',
}

export function useXsMobile() {
  return useMediaQuery(breakpoints.xs) // < 360px
}

export function useSmMobile() {
  return useMediaQuery(breakpoints.sm) // 360px - 479px
}

export function useMdMobile() {
  return useMediaQuery(breakpoints.md) // 480px - 639px
}

export function useTablet() {
  return useMediaQuery(breakpoints.tablet) // 640px - 767px
}

export function useLaptop() {
  return useMediaQuery(breakpoints.laptop) // 768px - 1023px
}

export function useDesktop() {
  return useMediaQuery(breakpoints.desktop) // 1024px - 1279px
}

export function useLargeDesktop() {
  return useMediaQuery(breakpoints.xl) // 1280px+
}

export function useXxlDesktop() {
  return useMediaQuery(breakpoints.xxl) // 1440px+
}

// Convenience hook to check if on any mobile size
export function useMobile() {
  return useMediaQuery('(max-width: 639px)') // < 640px (any mobile)
}

export function useIsAbove1024() {
  return useMediaQuery('(min-width: 1024px)')
}

export function useIsAbove768() {
  return useMediaQuery('(min-width: 768px)')
}

export function useIsAbove480() {
  return useMediaQuery('(min-width: 480px)')
}
